// =============================================================================
// deliveryOrderRow.js — the PURE half of the outbound courier hand-off write.
//
// Shapes a submitted hand-off into the `delivery_orders` row, builds the storage
// keys for its photos, and decides the one thing that actually matters here:
// **the idempotency key that makes the same hand-off appear exactly once.**
//
// No Supabase import, no network, no browser — so all of that can be tested
// hermetically. deliveryOrdersSync.js is the thin thing that makes the calls.
//
// WHY APPEAR-ONCE IS THE WHOLE JOB HERE. The 12–14 May triplicate-orders bug
// lived in this exact path: a silent sheet-routing fallback meant one shop's
// orders came back three times. Migration 0043 already makes the read-side
// version of that impossible (one shared table, filtered by shop). This file is
// the write-side half: the same hand-off, submitted twice, is one row.
// =============================================================================
import { extFor } from "./deliveryPhotoKey.js";

const safe = (s, max) => String(s == null ? "" : s).replace(/[^a-z0-9]+/gi, "_").slice(0, max);
const num = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? null : Number(v));

// Mirrors stockSync.idem (join + 200-char cap); inlined so this stays dependency-free.
function idem(...parts) {
  return parts.filter((p) => p != null && p !== "").join("|").slice(0, 200);
}

// ---- the platform ------------------------------------------------------------
// The till sends a human label ("Uber Eats"); the table joins on a key
// ("uber_eats"). Normalising rather than looking up keeps this pure and offline,
// and the three seeded keys fall straight out of it: Uber Eats -> uber_eats,
// Deliveroo -> deliveroo, Just Eat -> just_eat. An unknown platform normalises
// to its own key rather than being dropped — a new courier should show up as
// itself, not as a blank.
export function platformKeyFor(label) {
  const k = String(label == null ? "" : label).trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return k || null;
}

// ---- where a photo lives -----------------------------------------------------
// Under an `outbound/` prefix, always. Inbound photos already sit at the bucket
// root (`<site>/<day>/…`, see deliveryPhotoKey.js) and the two share one bucket,
// so the prefix is what guarantees a courier photo can never land on top of a
// stock-arrival photo. It also makes "show me the courier proof" one listing.
//
// `uniq` is a per-photo token that is STABLE across a retry of the same submit
// (the client token), so re-sending the same hand-off re-uses the same keys
// rather than piling up copies of the same picture.
export function outboundPhotoKey({ siteKey, day, orderNo, idx = 0, uniq, mime }) {
  const site = safe(siteKey, 24) || "unknown";
  const d = safe(day, 12) || "nodate";
  const ord = safe(orderNo, 40) || "noorder";
  const u = safe(uniq, 40) || "nouniq";
  return `outbound/${site}/${d}/${ord}__${u}__${idx}.${extFor(mime)}`;
}

// ---- the key that makes it appear once ---------------------------------------
// Built from the till's OWN per-order token — a uuid minted when the form opens
// and regenerated after each successful submit — so Supabase and the Google Sheet
// agree on what "the same order" means rather than each having a private opinion.
// The shop is included so the key is readable and self-describing in a log.
export function outboundIdemKey(siteKey, clientToken) {
  if (!clientToken) return null;      // no token, no promise — the caller must not write
  return idem(siteKey, "outbound", clientToken);
}

// ---- the row -----------------------------------------------------------------
// What goes to the database, and nothing else.
//
// Deliberately NOT in here:
//   * `staff_id` — the database takes it from the login pass, which is what makes
//     "who handed it over" truthful rather than something a tablet asserted.
//   * `occurred_at` — server-stamped. A tablet that has been offline, or whose
//     clock is wrong, must not decide when an order left the shop.
//   * any PIN, staff name or email. Ever.
//
// `captured_at` IS carried, because that one is genuine camera metadata off the
// photo and is the thing a dispute actually turns on.
export function courierHandoffRow({
  siteId, siteKey, orderNo, platform, clientToken,
  photoPaths = [], legacyUrls = [], note = "", primary = {},
} = {}) {
  const order = String(orderNo == null ? "" : orderNo).trim().slice(0, 40);
  const idemKey = outboundIdemKey(siteKey, clientToken);
  if (!siteId || !order || !idemKey) return null;   // never write a row we can't key or identify

  const paths = (photoPaths || []).filter(Boolean);
  const legacy = (legacyUrls || []).filter(Boolean);

  return {
    site_id: siteId,
    site: siteKey,                    // the DB trigger folds the spelling
    order_no: order,
    platform_key: platformKeyFor(platform),
    photo_paths: paths.length ? paths : null,
    legacy_photo_urls: legacy.length ? legacy : null,
    note: note ? String(note) : null,
    captured_at: primary.capturedAt || null,
    gps_lat: num(primary.gpsLat),
    gps_lng: num(primary.gpsLng),
    exif_present: typeof primary.exifPresent === "boolean" ? primary.exifPresent : null,
    capture_source: primary.source || null,
    origin: "kiosk",
    idem_key: idemKey,
  };
}
